import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useFavorites } from '../contexts/FavoritesContext';
import { useNavigate } from 'react-router-dom';
import { FaHeart } from 'react-icons/fa';
import ProductCard from '../components/ProductCard';
import { getImageUrl } from '../utils/api';

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 100px;
  padding-bottom: 60px;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 36px;
  color: #333;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  
  svg {
    color: #ff6b35;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 18px;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  color: #ddd;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 10px;
`;

const EmptyText = styled.p`
  color: #666;
  font-size: 16px;
  margin-bottom: 30px;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 107, 53, 0.3);
  }
`;

const FavoritesPage = () => {
  const { items, removeFromFavorites, loadFavorites, loading } = useFavorites();
  const navigate = useNavigate();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoad(true);
      await loadFavorites();
      setIsInitialLoad(false);
    };
    fetchData();
  }, [loadFavorites]);

  const handleRemoveFromFavorites = async (productId) => {
    try {
      await removeFromFavorites(productId);
    } catch (error) {
      console.error('Error removing from favorites:', error);
      alert('Ошибка при удалении из избранного');
    }
  };

  const handleViewProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Преобразуем данные избранного в формат для ProductCard
  const formatProductForCard = (favoriteItem) => {
    // Данные из API содержат все поля товара напрямую (из JOIN запроса)
    const productId = favoriteItem.product_id || favoriteItem.id;
    const imageUrl = favoriteItem.featured_image ? getImageUrl(favoriteItem.featured_image) : null;
    
    // Обрабатываем images: если это строка, парсим; если массив, фильтруем дубликаты с featured_image
    let processedImages = [];
    if (favoriteItem.images) {
      if (typeof favoriteItem.images === 'string') {
        try {
          processedImages = JSON.parse(favoriteItem.images);
        } catch (e) {
          processedImages = [favoriteItem.images];
        }
      } else if (Array.isArray(favoriteItem.images)) {
        processedImages = favoriteItem.images;
      }
      
      // Преобразуем все изображения через getImageUrl и исключаем featured_image
      processedImages = processedImages
        .map(img => typeof img === 'string' ? getImageUrl(img) : img)
        .filter(img => img && img !== imageUrl);
    } else if (imageUrl) {
      // Если images нет, но есть imageUrl, не добавляем его в images (он уже в featured_image)
      processedImages = [];
    }
    
    return {
      id: productId,
      name: favoriteItem.name || 'Товар',
      brand: favoriteItem.brand_name || 'Без бренда',
      price: parseFloat(favoriteItem.price) || 0,
      oldPrice: favoriteItem.compare_price ? parseFloat(favoriteItem.compare_price) : null,
      code: favoriteItem.sku || `P-${productId}`,
      rating: favoriteItem.avg_rating ? parseFloat(favoriteItem.avg_rating) : 0,
      reviewsCount: favoriteItem.reviews_count ? parseInt(favoriteItem.reviews_count, 10) : 0,
      description: favoriteItem.short_description || favoriteItem.description || '',
      image: imageUrl || '/nofoto.png',
      featured_image: imageUrl || '/nofoto.png',
      images: processedImages,
      category: favoriteItem.category_slug || 'other',
      inStock: true,
      isNew: false,
      created_at: favoriteItem.created_at,
      points: favoriteItem.points || 0,
      labels: favoriteItem.labels || []
    };
  };

  // Показываем загрузку только при первой загрузке
  if (isInitialLoad && loading) {
    return (
      <Container>
        <Content>
          <Header>
            <Title>Загрузка...</Title>
          </Header>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <Header>
          <Title>
            <FaHeart />
            Избранное
          </Title>
          <Subtitle>
            {items.length > 0 
              ? `У вас ${items.length} ${items.length === 1 ? 'товар' : items.length < 5 ? 'товара' : 'товаров'} в избранном`
              : 'Ваши избранные товары появятся здесь'}
          </Subtitle>
        </Header>

        {!isInitialLoad && items.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <FaHeart />
            </EmptyIcon>
            <EmptyTitle>Избранное пусто</EmptyTitle>
            <EmptyText>
              Добавьте товары в избранное, чтобы не потерять их
            </EmptyText>
            <ActionButton onClick={() => navigate('/catalog')}>
              Перейти в каталог
            </ActionButton>
          </EmptyState>
        ) : !isInitialLoad && items.length > 0 ? (
          <ProductsGrid>
            {items.map((favoriteItem) => {
              const product = formatProductForCard(favoriteItem);
              const productId = product.id;
              
              return (
                <ProductCard
                  key={productId}
                  product={product}
                  onToggleFavorite={() => handleRemoveFromFavorites(productId)}
                  onQuickView={() => handleViewProduct(productId)}
                />
              );
            })}
          </ProductsGrid>
        ) : null}
      </Content>
    </Container>
  );
};

export default FavoritesPage;

